import { useCallback, useSyncExternalStore } from 'react';

import { useTypedSelector } from 'hooks';

import { initialWizardState, WizardState } from './wizardModel';

export const ORBIT_WIZARD_STORAGE_PREFIX = 'orbit.wizard';

export const orbitWizardStorageKey = (tenantId: number | string, userId: number | string): string =>
  `${ORBIT_WIZARD_STORAGE_PREFIX}.${tenantId}.${userId}`;

/*
  The wizard's answers outlive the wizard: asking Orbit a question, leaving the page or reloading it
  all resume where the user stopped. Scoped per tenant and user like the Orbit session; every
  storage access is guarded, as private mode and a full quota both throw.
*/
const drafts = new Map<string, WizardState>();

// The keys written so far, so sign-out can find every draft without enumerating the storage.
const DRAFT_KEYS = `${ORBIT_WIZARD_STORAGE_PREFIX}.keys`;

const storedKeys = (): string[] => {
  const stored = localStorage.getItem(DRAFT_KEYS);
  return stored === null ? [] : (JSON.parse(stored) as string[]);
};

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const load = (key: string): WizardState => {
  try {
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      const parsed = JSON.parse(stored) as Partial<WizardState>;
      const initial = initialWizardState();
      // A draft from before a field was added still reads with that field's default.
      return { ...initial, ...parsed, game: { ...initial.game, ...parsed.game } };
    }
  } catch {
    // An unreadable draft starts the wizard afresh.
  }
  return initialWizardState();
};

const read = (key: string): WizardState => {
  const cached = drafts.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const loaded = load(key);
  drafts.set(key, loaded);
  return loaded;
};

const write = (key: string, state: WizardState) => {
  drafts.set(key, state);
  try {
    localStorage.setItem(key, JSON.stringify(state));
    const keys = storedKeys();
    if (!keys.includes(key)) {
      localStorage.setItem(DRAFT_KEYS, JSON.stringify([...keys, key]));
    }
  } catch {
    // The draft still lives for this page visit.
  }
  listeners.forEach(listener => listener());
};

/** Sign-out drops every stored draft, so nothing carries over to the next user. */
export const clearOrbitWizardDrafts = () => {
  drafts.clear();
  try {
    storedKeys().forEach(key => localStorage.removeItem(key));
    localStorage.removeItem(DRAFT_KEYS);
  } catch {
    // Nothing more can be removed.
  }
  listeners.forEach(listener => listener());
};

export interface CampaignWizardStore {
  state: WizardState;
  update: (change: (state: WizardState) => WizardState) => void;
  reset: () => void;
}

export const useCampaignWizard = (): CampaignWizardStore => {
  const tenantId = useTypedSelector(state => state.global?.tenantConfig?.id);
  const userId = useTypedSelector(state => state.global?.authUser?.id);
  const key =
    tenantId && userId ? orbitWizardStorageKey(tenantId, userId) : ORBIT_WIZARD_STORAGE_PREFIX;
  const getSnapshot = useCallback(() => read(key), [key]);
  const state = useSyncExternalStore(subscribe, getSnapshot);

  const update = useCallback(
    (change: (current: WizardState) => WizardState) => write(key, change(read(key))),
    [key]
  );
  const reset = useCallback(() => write(key, initialWizardState()), [key]);

  return { state, update, reset };
};
